/**
 * Whether a conditional piece of a spec ships, and which word it takes.
 *
 * Catalogue templates used to gate fields on onboarding answers. Those
 * questions are gone. Apply still calls these so a composed or blank spec
 * with no conditions is unchanged: null tags ship everything, and pickKey
 * returns the base word.
 */

/** What a caller used to send as questionId → answer ids. Unused on the wire. */
export type OnboardingAnswers = Readonly<Record<string, readonly string[]>>;

/** `trade:food`. The one form a spec condition ever named. */
export const answerTag = (questionId: string, answerId: string): string =>
  `${questionId}:${answerId}`;

/** No catalogue questions remain, so no tag is known. */
export const ANSWER_TAGS: readonly string[] = [];

export const isAnswerTag = (value: string): boolean => ANSWER_TAGS.includes(value);

/**
 * The answers as a tag set, or null when the caller gave none (or none that
 * are still known). Null is not an empty set: with no tags every conditional
 * field ships.
 */
export const answerTags = (
  answers: OnboardingAnswers | null | undefined,
): ReadonlySet<string> | null => {
  if (answers === null || answers === undefined) return null;
  const tags = new Set<string>();
  for (const [questionId, picked] of Object.entries(answers)) {
    for (const answerId of picked ?? []) {
      const tag = answerTag(questionId, answerId);
      if (isAnswerTag(tag)) tags.add(tag);
    }
  }
  return tags.size === 0 ? null : tags;
};

/** Whether a thing carrying `whenAnswer` ships against this tag set. */
export const shipsWhen = (
  whenAnswer: readonly string[] | undefined,
  tags: ReadonlySet<string> | null,
): boolean => {
  if (whenAnswer === undefined || whenAnswer.length === 0) return true;
  if (tags === null) return true;
  return whenAnswer.some((tag) => tags.has(tag));
};

/**
 * The word this answer set asks for, or the plain key.
 *
 * Declaration order decides a tie. With no tags the base key always wins.
 */
export const pickKey = (
  base: string,
  by: Readonly<Record<string, string>> | undefined,
  tags: ReadonlySet<string> | null,
): string => {
  if (by === undefined || tags === null) return base;
  for (const [tag, key] of Object.entries(by)) {
    if (tags.has(tag)) return key;
  }
  return base;
};
